// Stale-deploy chunk handling, shared by index.tsx (global window listeners),
// App.tsx (Sentry beforeSend) and common/LazyLoader.tsx.
//
// After a web deploy, a browser holding stale cached HTML requests hashed
// chunk files (JS or CSS) that no longer exist, producing a ChunkLoadError /
// "Loading chunk N failed". React.lazy import failures surface as rejected
// promises, so callers must also listen for 'unhandledrejection'.
//
// Recovery: reload once to fetch the fresh index. The guard is a timestamp,
// not a one-shot boolean, so a genuinely-persistent broken chunk cannot
// infinite-reload (bounded to one reload per cooldown), while a later deploy
// in the same session can still recover once the cooldown elapses.

const CHUNK_RELOAD_AT_KEY = 'chunkReloadAt';
const RELOAD_COOLDOWN_MS = 10000;

const CHUNK_MESSAGE = /Loading (CSS )?chunk .+ failed/i;

/**
 * True when the given error (or message string) is a stale-deploy chunk load
 * failure. Accepts an Error, a Sentry hint's originalException, or a string.
 */
export const isChunkLoadError = (error: unknown): boolean => {
  if (!error) return false;
  if (typeof error === 'string') return CHUNK_MESSAGE.test(error);
  const err = error as {name?: unknown; message?: unknown};
  if (err.name === 'ChunkLoadError') return true;
  return typeof err.message === 'string' && CHUNK_MESSAGE.test(err.message);
};

/**
 * If `error` is a stale-deploy chunk failure, reload once to pick up the fresh
 * build. Guarded by a sessionStorage timestamp so it cannot loop. No-op in
 * development. Returns true when a reload was triggered.
 */
export const reloadOnceOnStaleChunk = (error: unknown): boolean => {
  if (process.env.NODE_ENV === 'development') return false;
  if (!isChunkLoadError(error)) return false;

  const last = Number(sessionStorage.getItem(CHUNK_RELOAD_AT_KEY)) || 0;
  if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;

  sessionStorage.setItem(CHUNK_RELOAD_AT_KEY, String(Date.now()));
  window.location.reload();
  return true;
};
